import type React from "react";

/**
 * DocumentSection — bloco de seção pro entregável (Resultado).
 *
 * Estilo Apple Notes / Linear: título kicker pequeno + body com lots of
 * whitespace, separator sutil entre seções. SEM borda em volta.
 */

/**
 * DocumentSection — segue o padrão V1 do protótipo (documento editorial):
 * título wf-display médio, body em texto fluído. Sem caixa em volta;
 * separação por whitespace.
 */
export function DocumentSection({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginBottom: 24 }}>
      <h2
        className="wf-display"
        style={{
          fontSize: 26,
          fontWeight: 700,
          letterSpacing: "-0.015em",
          lineHeight: 1.15,
          marginBottom: 12,
          marginTop: 0,
          color: "var(--nd-orange-pun)", // laranja ND #ff5000 nos títulos
        }}
      >
        {title}
      </h2>
      <div
        style={{
          fontSize: 15,
          lineHeight: 1.65,
          color: "var(--wf-ink)",
        }}
      >
        {children}
      </div>
    </section>
  );
}

/**
 * MetricGrid — grade de números/KPIs sem caixa, só whitespace.
 */
export interface Metric {
  label: string;
  value: string;
  delta?: string;
  positive?: boolean;
}

/**
 * MetricGrid — KPIs em cards pequenos, igual o V1 do protótipo.
 * Borda fina, padding apertado, número em wf-display.
 */
export function MetricGrid({ metrics }: { metrics: Metric[] }) {
  if (metrics.length === 0) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(140px, 1fr))`,
        gap: 10,
      }}
    >
      {metrics.map((m, i) => (
        <div
          key={i}
          className="wf-card"
          style={{ padding: 12 }}
        >
          <div
            style={{
              fontSize: 11,
              color: "var(--wf-ink-3)",
              marginBottom: 2,
              fontWeight: 500,
            }}
          >
            {m.label}
          </div>
          <div
            className="wf-display"
            style={{
              fontSize: 22,
              lineHeight: 1.1,
              fontWeight: 700,
              letterSpacing: "-0.015em",
              color: "var(--wf-ink)",
            }}
          >
            {m.value}
          </div>
          {m.delta && (
            <div
              style={{
                fontSize: 12,
                color: m.positive ? "#1f5c1f" : "var(--nd-orange-pun)",
                marginTop: 2,
                fontWeight: 500,
              }}
            >
              {m.delta}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * NumberedList — segue V1 protótipo: círculo laranja com número branco,
 * separadores tracejados entre items.
 */
export function NumberedList({
  items,
  renderItem,
}: {
  items: string[];
  renderItem?: (item: string) => React.ReactNode;
}) {
  return (
    <ol
      style={{
        listStyle: "none",
        padding: 0,
        margin: 0,
      }}
    >
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: "flex",
            gap: 12,
            padding: "10px 0",
            borderBottom: i < items.length - 1 ? "1px dashed var(--wf-ink-4)" : "none",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: "var(--nd-orange)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--wf-font-display)",
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {i + 1}
          </div>
          <div
            style={{
              flex: 1,
              fontSize: 14,
              lineHeight: 1.55,
              color: "var(--wf-ink)",
              paddingTop: 4,
            }}
          >
            {renderItem ? renderItem(item) : item}
          </div>
        </li>
      ))}
    </ol>
  );
}
